import React, { useEffect, useRef } from 'react';
import './AuctionModal.css';
import OwnerBadge from '../Common/OwnerBadge';
import { pushModalToStack, removeModalFromStack, isTopModal } from '../Common/modalStack';

const getAuctionDisplayNumber = (auctionLike) => {
  if (!auctionLike) return '';
  return auctionLike.owner_sequence || auctionLike.auction_owner_sequence || auctionLike.id || auctionLike.auction_id || '';
};

const AuctionModal = ({ 
  isOpen, 
  onClose, 
  auction, 
  debtors, 
  organizers, 
  onSubmit, 
  isViewMode = false, 
  onEdit,
  onSwitchToViewMode,
  hasUnsavedChanges = false,
  titleValue = '',
  onTitleInput = null,
  isTitleEditable = false,
  children 
}) => {
  const overlayClickRef = useRef(false);
  const modalIdRef = useRef(`auction_modal_${Date.now()}_${Math.random().toString(16).slice(2)}`);

  useEffect(() => {
    const id = modalIdRef.current;
    if (isOpen) {
      pushModalToStack(id);
    }
    return () => {
      removeModalFromStack(id);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        if (!isTopModal(modalIdRef.current)) return;
        // Если поверх открыта другая модалка (modal-overlay), не закрываем торги.
        if (document.querySelector('.modal-overlay')) {
          return;
        }
        // Сразу проверяем, есть ли открытые диалоги подтверждения
        const confirmDialogs = document.querySelectorAll('.confirm-dialog, .unsaved-changes-popover');
        if (confirmDialogs.length > 0) {
          return; // Не закрываем модальное окно, если есть открытый диалог подтверждения
        }
        
        // Если мы в режиме редактирования и есть несохраненные изменения
        if (!isViewMode && hasUnsavedChanges) {
          // Используем логику onClose, которая покажет диалог подтверждения
          onClose({ source: 'escape' });
        } else if (!isViewMode && auction && onSwitchToViewMode) {
          // Если мы в режиме редактирования без изменений, переключаемся в режим просмотра
          onSwitchToViewMode();
        } else {
          // Если мы в режиме просмотра или создания новых торгов, закрываем модальное окно
          onClose({ source: 'escape' });
        }
      }
    };

    if (isOpen) {
      // Используем обычную фазу, но с низким приоритетом
      document.addEventListener('keydown', handleEscape, false);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape, false);
    };
  }, [isOpen, onClose, isViewMode, auction, onSwitchToViewMode, hasUnsavedChanges]);

  if (!isOpen) return null;

  const getTitle = () => {
    if (!auction) return 'Создание новых торгов';
    const auctionTitle = String(auction?.title || '').trim() || `Торги #${getAuctionDisplayNumber(auction)}`;
    return isViewMode ? auctionTitle : 'Редактирование торгов';
  };

  return (
    <div
      className="auction-modal-overlay"
      onMouseDown={(e) => {
        overlayClickRef.current = e.target === e.currentTarget;
      }}
      onClick={() => {
        if (!overlayClickRef.current) return;
          onClose({ source: 'escape' });
      }}
    >
      <div className="auction-modal" onClick={(e) => e.stopPropagation()}>
        <div className="auction-modal-header">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minWidth: 0 }}>
            {isTitleEditable ? (
              <input
                key={`auction-title-input-${auction?.id || 'new'}`}
                type="text"
                value={titleValue}
                onChange={(e) => onTitleInput && onTitleInput(e.currentTarget.value)}
                placeholder={auction ? `Торги #${getAuctionDisplayNumber(auction)}` : 'Введите наименование торгов'}
                style={{
                  margin: 0,
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  lineHeight: 1.3,
                  border: '1px solid #dcdfe3',
                  borderRadius: 8,
                  padding: '8px 10px',
                  minWidth: 320,
                  width: '100%',
                  maxWidth: 'none',
                }}
              />
            ) : (
              <h2 style={{ margin: 0 }}>{getTitle()}</h2>
            )}
            {!!auction && <OwnerBadge email={auction.owner_email} />}
          </div>
          <div className="auction-modal-header-actions">
            {auction && isViewMode && (
              <button type="button" className="btn btn-primary" onClick={onEdit}>
                Редактировать
              </button>
            )}
            <button className="auction-modal-close" onClick={onClose}>
              <span>&times;</span>
            </button>
          </div>
        </div>
        <div className="auction-modal-body">
          {children}
        </div>
      </div>
    </div>
  );
};

export default AuctionModal;
